import { FinancialType } from '@/components/_common/FinancialTypeGenerate';
import { DocumentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import { PaymentCondition } from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.types';
import { MathRound } from '@/utils/functions/MathRound';

interface Params {
  documentType: DocumentType;
  isInstallmentFree: boolean;
  installmentQuantity: number;
  paymentCondition?: PaymentCondition;
  valueTotal: number;
  financialType: FinancialType;
  index: number;
  increaseDecrease: number;
}

export function formatToRealValue(value: number) {
  return Math.round(value * 1000) % 10 > 5
    ? parseFloat(value.toFixed(2))
    : Math.floor(value * 100) / 100;
}

export function getInstallmentValues({
  documentType,
  isInstallmentFree,
  installmentQuantity,
  paymentCondition,
  valueTotal,
  financialType,
  index,
  increaseDecrease,
}: Params) {
  const installmentPercentage = isInstallmentFree
    ? 100 / (installmentQuantity || 1)
    : paymentCondition?.installments[index].percentage || 100;

  let remainingAmount = 0;

  if (isInstallmentFree) {
    remainingAmount = MathRound(
      valueTotal -
        MathRound(valueTotal / installmentQuantity, 2) * installmentQuantity,
      2,
    );
  } else {
    remainingAmount = MathRound(
      valueTotal -
        ((paymentCondition?.installments.reduce(
          (acc, curr) =>
            acc + MathRound((valueTotal * curr.percentage) / 100, 2),
          0,
        ) || 0) /
          installmentQuantity) *
          installmentQuantity,
      2,
    );
  }

  const indexVerify =
    financialType === FinancialType.Stone ? 0 : installmentQuantity - 1;

  const installmentValue =
    index === indexVerify
      ? MathRound(valueTotal * (installmentPercentage / 100), 2) +
        remainingAmount
      : MathRound(valueTotal * (installmentPercentage / 100), 2);

  const discountIncrease = MathRound(
    (installmentValue / 100) * (paymentCondition?.percentage || 0) +
      increaseDecrease,
    2,
  );

  const receivableValue = installmentValue + discountIncrease;

  let tariff = 0;

  //Taxa Fixa
  if (documentType.fixedRate) {
    tariff = MathRound((documentType.percentRate / 100) * receivableValue, 2);
  }

  // Taxa variável
  else if (documentType.rates.length) {
    const rate = documentType.rates?.find(
      ({ numberInstallments }) => numberInstallments === installmentQuantity,
    );

    if (rate) {
      const percentageRate = rate?.percentRate / 100;

      const indexVerify =
        financialType === FinancialType.Stone ? 0 : installmentQuantity - 1;

      const installmentBaseValue = MathRound(
        valueTotal / installmentQuantity,
        2,
      );

      const totalTariff = MathRound(valueTotal * percentageRate, 2);

      const defaultTariff = MathRound(installmentBaseValue * percentageRate, 2);

      const tariffDifference = MathRound(
        totalTariff - defaultTariff * installmentQuantity,
        2,
      );

      tariff =
        index === indexVerify
          ? MathRound(defaultTariff + tariffDifference, 2)
          : defaultTariff;
    } else {
      tariff = 0;
    }
  }

  const realValue = installmentValue + discountIncrease - tariff;

  return {
    realValue,
    receivableValue,
    installmentValue,
    discountIncrease,
    tariff,
  };
}
